import React from "react";
import Link from "../components/Link";

export default function PDFSDKCopy() {
  return {
    meta: {
      title: "PDF SDK: Fully Customizable, Add 100s of Features | PDFTron",
      description: "Customize our PDF SDK to create a simplified UI for non-tech savy users. Add 100's of features: View, Convert, Forms, Create, Edit, Manipulate, Extract, Sign"
    },
    hero: {
      title: 'The Complete PDF SDK',
      subtitle: 'Bring accurate PDF viewing, annotating, editing, creation, and generation to any web, mobile, desktop or server framework or application. Own the full document and data lifecycle by deploying on your own infrastructure without worrying about third-party server dependencies.',
      image: '/assets/hero/pdf-sdk-hero.svg',
      primaryAction: {
        label: 'Launch Demo',
        to: '/webviewer/demo'
      },
      secondaryAction: {
        label: 'See Capabilities',
        to: '/products'
      }
    },
    valueProps: {
      title: 'Why PDFTron?',
      items: [
        {
          title: 'CLIENT-SIDE PROCESSES',
          description: 'Scale easily without any server-side dependencies like Microsoft Office or LibreOffice for rendering, conversion, or editing PDFs, Microsoft Office, images, videos, and HTML',
          icon: '/assets/pdfsdk/client-side-process-icon.svg',
        },
        {
          title: 'BEST-IN-CLASS RENDERING QUALITY',
          description: 'Built in-house without reliance on any open-source technologies, providing you the most accurate rendering at any zoom level',
          icon: '/assets/pdfsdk/best-in-class-rendering-quality-icon.svg',
        },
        {
          title: 'BULLETPROOF SECURITY',
          description: 'Complete control over document workflow by deploying on your own infrastructure without data ever leaving your platform',
          icon: '/assets/pdfsdk/bulletproof-security.svg',
        },
        {
          title: 'EXPERT SUPPORT',
          description: 'Our team of experienced SDK developers help you get up to speed with PDFTron, set up the solution as part of the unlimited trial for as long as you need, and answer any questions',
          icon: '/assets/pdfsdk/expert-support.svg',
        }
      ],
      // links to testimonials.ts
      testimonialKey: 'Marcus O’Brien'
    },
    languageSupport : {
      title: 'Languages',
      icons: [
        'js',
        'ts',
        'swift',
        'dotnet',
        'dotnetcore',
        'java',
        'cpp',
        'nodejs',
        'python',
        'go',
        'ruby',
        'php'
      ]
    },
    additionalValueProps: [
      {
        title: 'Client-side and Server-side',
        description: 'Users can annotate, collaborate, fill forms, sign PDFs entirely client-side with an open-source UI driven by customer feedback. Perform the same functionality programmatically client-side or server-side.',
        icon: '/assets/pdfsdk/client-server-side-logo.svg'
      }
    ],
    features: {
      title: 'PDF SDK Features',
      items: [
        {
          title: 'Viewing and Editing',
          description: 'Bring your users secure, scalable and high-fidelity viewing and precision editing capabilities for PDF and other electronic documents across web, mobile, and desktop platforms.',
          icon: '/assets/pdfsdk/viewing-editing.svg',
          linkLabel: 'Learn More',
          to: '/capabilities/viewing/'
        },
        {
          title: 'Annotation and Collaboration',
          description: 'Empower your users with real-time collaboration support for markup, comment, measurement, and countless other custom annotations. Add custom workflows and approval logic with annotation statuses.',
          icon: '/assets/pdfsdk/annotation-collaboration.svg',
          linkLabel: 'Learn More',
          to: '/pdf-sdk/annotation-library/'
        },
        {
          title: 'Signing',
          description: 
            <>
              Create custom signing workflows, including placing signature fields, assigning signers and collecting signatures. Certify, <Link to='/blog/webviewer/webviewer-7-3/' newTab blue>validate</Link>, and seal digitally signed documents.
            </>,
          icon: '/assets/pdfsdk/signing.svg',
          linkLabel: 'Learn More',
          to: '/pdf-sdk/digital-signature-library/'
        },
        {
          title: 'Page Manipulation',
          description: 'Insert, remove, or rearrange pages. Assemble, merge, or split documents directly in the browser. View multiple documents in the same viewer or side by side.',
          icon: '/assets/pdfsdk/page-manipulation.svg',
          linkLabel: 'Learn More',
          to: '/pdf-sdk/manipulation-library/'
        },
        {
          title: 'Form Fill, Edit, and Create',
          description:            
          <>
            Users fill, edit, or create new forms themselves, or prepopulate data from any existing source into <Link to='/blog/webviewer/webviewer-7-1/#pdf-form-editor' newTab blue>forms</Link> stored on any storage provider, such as AWS, Azure, GCP, and others. 
          </>,

          icon: '/assets/pdfsdk/form-fill-edit-create.svg',
          linkLabel: 'Learn More',
          to: '/pdf-sdk/form-filler/'
        },
        {
          title: 'True Redaction',
          description: 'Search for and permanently remove sensitive information from documents, such as personally identifiable information or protected health information while keeping documents searchable.',
          icon: '/assets/pdfsdk/true-redaction.svg',
          linkLabel: 'Learn More',
          to: '/capabilities/redaction/'
        },
        {
          title: 'Template Generation',
          description: 'Generate documents by populating PDF, DOCX, PPTX, XLSX, or HTML templates with data from existing data storage.',
          icon: '/assets/pdfsdk/template-generation.svg',
          linkLabel: 'Learn More',
          to: '/capabilities/document-generation/'
        },
        {
          title: 'Measurement Tools',
          description: 'Define a document’s scale and measure distance, area, and perimeter. Use snap-to control points for maximum precision.',
          icon: '/assets/pdfsdk/measurement-tools.svg',
          linkLabel: 'Learn More',
          to: '/capabilities/measurement/'
        },
        {
          title: 'PDF/A Library',
          description: 'Convert PDF and any other file formats into ISO-compliant PDF/A documents supporting various levels of compliance, including VeraPDF validations and to generate ZUGFeRD electronic invoices.',
          icon: '/assets/pdfsdk/pdfa-library.svg',
          linkLabel: 'Learn More',
          to: '/pdf-sdk/pdfa-converter-library/'
        }
      ],
      primaryAction: {
        label: 'See all Capabilities ',
        to: '/products'
      }
    },
    generalTestimonial: {
      title: 'A Partner You Can Trust',
      description: 'The PDFTron SDK powers thousands of software applications across startups, governments, and Fortune 100 companies worldwide in virtually every industry. While we support other formats, PDF is the backbone that everyone needs to be able to rely on.',
      linkLabel: 'Our Customers',
      to: 'https://www.pdftron.com/customers/'
    },
    customerTestimonialKeys: [
      // keys are in testimonials.ts
      'Alistair Michener',
      'Sudeep Venkatesh'
    ]
  };
}